import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import IndexPage from 'flarum/forum/components/IndexPage';

import addHeaderNavigation from './addHeaderNavigation';
import addReportControl from './addReportControl';
import addReviewNavigation from './addReviewNavigation';
import addMessageNavigation from './addMessageNavigation';
import PrivateMessagesPage from './pages/PrivateMessagesPage';
import MessageReportsPage from './pages/MessageReportsPage';
import PrivateMessageNotification from './components/PrivateMessageNotification';
import MessageReportNotification from './components/MessageReportNotification';
import ReportThresholdNotification from './components/ReportThresholdNotification';
import ReportResolvedNotification from './components/ReportResolvedNotification';
import ReviewTaskPage from './components/ReviewTaskPage';
import HomeHero from './components/HomeHero';
import SectionGrid from './components/SectionGrid';
import QuarterlyChallenge from './components/QuarterlyChallenge';
import LatestDiscussionsHeading from './components/LatestDiscussionsHeading';
import { isHomepagePath } from './utils/homeState.mjs';
import { emptyUnreadCounts, fetchUnreadCounts } from './utils/unreadCounts.mjs';

let unreadCounts = emptyUnreadCounts();
let unreadCountsUserId = null;
let unreadCountsRequest = null;

const isHomepage = () => {
  const currentPath = window.location.pathname + window.location.search;

  return isHomepagePath(currentPath, app.forum.attribute('basePath') || '');
};

const currentUserId = () => (app.session.user ? app.session.user.id() : null);

const loadUnreadCounts = (force = false) => {
  const userId = currentUserId();

  if (!userId) {
    unreadCounts = emptyUnreadCounts();
    unreadCountsUserId = null;
    return Promise.resolve(unreadCounts);
  }

  if (unreadCountsRequest) return unreadCountsRequest;
  if (!force && unreadCountsUserId === userId) return Promise.resolve(unreadCounts);

  unreadCountsUserId = userId;
  unreadCountsRequest = fetchUnreadCounts(app)
    .then((counts) => {
      if (currentUserId() === userId) unreadCounts = counts;
      return unreadCounts;
    })
    .finally(() => {
      unreadCountsRequest = null;
      m.redraw();
    });

  return unreadCountsRequest;
};

app.initializers.add('yingtao-forum-homepage', () => {
  app.routes.yingtaoReviewTasks = { path: '/review-reports', component: ReviewTaskPage };
  app.routes.yingtaoMessages = { path: '/messages', component: PrivateMessagesPage };
  app.routes.yingtaoMessageReports = { path: '/review-message-reports', component: MessageReportsPage };

  addHeaderNavigation();
  addReportControl();
  addReviewNavigation();
  addMessageNavigation();
  app.notificationComponents.yingtaoPrivateMessage = PrivateMessageNotification;
  app.notificationComponents.yingtaoMessageReport = MessageReportNotification;
  app.notificationComponents.yingtaoReportThreshold = ReportThresholdNotification;
  app.notificationComponents.yingtaoReportResolved = ReportResolvedNotification;

  window.addEventListener('focus', () => {
    if (isHomepage()) loadUnreadCounts(true);
  });

  extend(IndexPage.prototype, 'oninit', function () {
    if (isHomepage()) loadUnreadCounts(true);
  });

  override(IndexPage.prototype, 'hero', function (original) {
    if (!isHomepage()) return original();

    return <HomeHero onStartDiscussion={() => this.newDiscussionAction().catch(() => {})} />;
  });

  extend(IndexPage.prototype, 'view', function (vdom) {
    if (!isHomepage() || !vdom) return;

    vdom.attrs = vdom.attrs || {};
    vdom.attrs.className = [vdom.attrs.className, 'IndexPage--yingtaoHome'].filter(Boolean).join(' ');
  });

  extend(IndexPage.prototype, 'contentItems', function (items) {
    if (!isHomepage()) return;

    const startDiscussion = () => this.newDiscussionAction().catch(() => {});
    loadUnreadCounts();

    items.add(
      'yingtaoSections',
      <SectionGrid onStartDiscussion={startDiscussion} unreadCounts={unreadCounts} />,
      130
    );
    items.add(
      'yingtaoQuarterlyChallenge',
      <QuarterlyChallenge unreadCount={unreadCounts['quarterly-challenge']} />,
      120
    );
    items.add('yingtaoLatestDiscussionsHeading', <LatestDiscussionsHeading />, 110);
  });
}, -10);
